"use client";

import { CheckCircle2 } from "lucide-react";
import { FormEvent, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { createClientId } from "@/lib/client-id";

type InquiryDraft = {
  firstName: string; lastName: string; email: string; phone: string; preferredDate: string;
  startTime: string; endTime: string; partySize: string; eventType: string; notes: string;
};

const initialDraft = (): InquiryDraft => ({
  firstName: "", lastName: "", email: "", phone: "", preferredDate: "", startTime: "18:00", endTime: "21:00",
  partySize: "", eventType: "corporate", notes: "",
});

export function PrivateEventInquiry({ open, onOpenChange, venue }: {
  open: boolean; onOpenChange: (open: boolean) => void;
  venue: { tenantSlug: string; locationSlug: string; publicName: string };
}) {
  const [draft, setDraft] = useState(initialDraft);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [confirmation, setConfirmation] = useState<string | null>(null);

  const update = (field: keyof InquiryDraft, value: string) => setDraft((current) => ({ ...current, [field]: value }));
  const close = (next: boolean) => {
    onOpenChange(next);
    if (!next && confirmation) { setDraft(initialDraft()); setConfirmation(null); setError(""); }
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const response = await fetch("/api/tables/private-event-inquiries", {
        method: "POST",
        headers: { "content-type": "application/json", "idempotency-key": createClientId() },
        body: JSON.stringify({
          tenantSlug: venue.tenantSlug, locationSlug: venue.locationSlug, ...draft,
          partySize: Number(draft.partySize),
        }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.message ?? payload.detail ?? "Your inquiry could not be submitted.");
      setConfirmation(payload.publicId as string);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Your inquiry could not be submitted.");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="private-inquiry-dialog" aria-describedby="private-inquiry-description">
        {confirmation ? (
          <div className="private-inquiry-success">
            <CheckCircle2 />
            <p className="eyebrow">Inquiry received</p>
            <DialogTitle>We’ll be in touch.</DialogTitle>
            <DialogDescription id="private-inquiry-description">{venue.publicName} has your event details. The events team will follow up using the contact information you provided.</DialogDescription>
            <small>Reference {confirmation.slice(0, 8).toUpperCase()}</small>
            <Button type="button" onClick={() => close(false)}>Done</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <p className="eyebrow">Gather at {venue.publicName}</p>
              <DialogTitle>Plan a private event.</DialogTitle>
              <DialogDescription id="private-inquiry-description">Tell the events team what you have in mind. This is an inquiry, not a confirmed reservation.</DialogDescription>
            </DialogHeader>
            <form className="private-inquiry-form" onSubmit={submit}>
              <div className="private-inquiry-grid">
                <label>First name<Input required autoComplete="given-name" value={draft.firstName} onChange={(event) => update("firstName", event.target.value)} /></label>
                <label>Last name<Input required autoComplete="family-name" value={draft.lastName} onChange={(event) => update("lastName", event.target.value)} /></label>
                <label>Email<Input required type="email" autoComplete="email" value={draft.email} onChange={(event) => update("email", event.target.value)} /></label>
                <label>Phone<Input type="tel" autoComplete="tel" value={draft.phone} onChange={(event) => update("phone", event.target.value)} /></label>
                <label>Preferred date<Input required type="date" min={new Date().toISOString().slice(0, 10)} value={draft.preferredDate} onChange={(event) => update("preferredDate", event.target.value)} /></label>
                <label>Number of guests<Input required type="number" min="1" max="5000" value={draft.partySize} onChange={(event) => update("partySize", event.target.value)} /></label>
                <label>Start time<Input required type="time" step="900" value={draft.startTime} onChange={(event) => update("startTime", event.target.value)} /></label>
                <label>End time<Input required type="time" step="900" value={draft.endTime} onChange={(event) => update("endTime", event.target.value)} /></label>
                <label className="full">Event type<select value={draft.eventType} onChange={(event) => update("eventType", event.target.value)}><option value="anniversary">Anniversary</option><option value="shower">Shower</option><option value="birthday">Birthday</option><option value="corporate">Corporate event</option><option value="engagement">Engagement</option><option value="graduation">Graduation</option><option value="holiday">Holiday event</option><option value="rehearsal_dinner">Rehearsal dinner</option><option value="reception">Reception</option><option value="other">Other</option></select></label>
                <label className="full">Tell us about the event <span>{draft.notes.length}/5000</span><Textarea maxLength={5000} placeholder="Timing, budget, occasion, accessibility, or other needs" value={draft.notes} onChange={(event) => update("notes", event.target.value)} /></label>
              </div>
              {error && <p className="booking-error" role="alert">{error}</p>}
              <Button type="submit" disabled={submitting}>{submitting ? "Sending…" : "Send inquiry"}</Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
